import { ChangeEvent, ComponentPropsWithoutRef, ReactNode, useRef } from "react"

import { CancelIcon } from "./CancelIcon"
import InputErrors from "./InputErrors"
import styles from "./styles.module.css"

export const RIGHT_ICON_BUTTON_CLASSES =
  "flex items-center absolute right-0 bg-white dark:bg-transparent h-[90%] rounded"

export type BaseInputProps = ComponentPropsWithoutRef<"input"> & {
  id?: string
  name?: string
  value?: string
  defaultValue?: string
  label?: string
  errors?: string[]
  ariaLabel?: string
  onClear?: () => void
  placeholder?: string
  leftReactNode?: ReactNode
  rightReactNode?: ReactNode
  errorShiftCorrected?: boolean
  type: "text" | "email" | "password" | "search"
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void
  extraInputClasses?: Array<string>
}

export type ConcreteInputProps = Omit<
  BaseInputProps,
  "type" | "leftReactNode" | "rightReactNode"
>

export const BaseInput = ({
  id,
  label,
  value,
  onClear,
  leftReactNode,
  rightReactNode,
  ariaLabel,
  errors = [],
  extraInputClasses = [],
  errorShiftCorrected = true,
  defaultValue,
  type,
  placeholder,
  name,
  onChange,
  onBlur,
  ...restProps
}: BaseInputProps) => {
  const showError = Boolean(errors.length)
  const inputElementRef = useRef<HTMLInputElement>(null)
  const showRightReactNode = rightReactNode || (value && onClear)
  const inputClasses = `py-3.5 sm:py-3 grow outline-none ${
    styles.input
  } ${extraInputClasses.join(" ")} ${
    showError ? "text-stears_danger text-xs" : ""
  } `.trim()

  const inputWrapperClasses = `relative flex items-center bg-stears_black ${
    showError
      ? "border-stears_danger"
      : " hover:border-gray-600 focus-within:border-stears_black hover:border-stears_black"
  }`.trim()

  return (
    <div className="flex flex-col relative text-base font-jakarta">
      {label && (
        <label
          className="mb-2 text-stears_black dark:block dark:text-xs dark:text-[#9CA3AF]"
          htmlFor={id}
        >
          {label}
        </label>
      )}
      <div className={inputWrapperClasses}>
        {leftReactNode}
        <input
          id={id}
          value={value}
          aria-label={ariaLabel}
          defaultValue={defaultValue}
          type={type}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          ref={inputElementRef}
          className={`indent-3.5 ${inputClasses} dark:bg-[#1F2937] h-[38px] border-[0.5px]  rounded-0 text-sm text-[#E5E7EB] placeholder:text-xs placholder:text-[#E5E7EB]  ${
            showError ? "dark:border-red-500" : "border-[#4B5563]"
          }`}
          {...restProps}
        />
        {showRightReactNode &&
          (rightReactNode || (
            <button
              className={`${RIGHT_ICON_BUTTON_CLASSES} mr-4`}
              onClick={() => {
                onClear && onClear()
                inputElementRef.current?.focus()
              }}
            >
              <CancelIcon />
            </button>
          ))}
      </div>
      {showError ? (
        <InputErrors errors={errors} name={name as string} />
      ) : errorShiftCorrected ? null : null}
    </div>
  )
}
